import * as React from 'react'
import { Paths } from '@logic'
import { FieldGroup, protectedInputFieldComponent, renderFieldsSimple, textInputFieldComponentAdvanced } from '@shared/components/forms'
import { isEmail } from '@shared/helpers'
import { emptyValidator } from '@shared/helpers/field-validators'
import styled from 'styled-components'
import { FlexCol } from '@src/styles'
import { Button, LoginSignButton } from '@shared/components/button'
import { Link } from 'react-router-dom'
import { LogoHeading } from './logo-heading'
import { GoogleAuthButton } from './google-auth-button'

interface Props {
    invalid?: boolean
    values?: any
    form?: {
        change: (name: string, value: any) => void
    }
}

const LoginFormWrapper = styled.div<any>`
.LoginText{
    margin: 32px 0px 24px 0px;
    font-size: 34px;
    line-height: 46px;
    font-weight: 600;
}
@keyframes autofill-detect {
    from { opacity: 1; }
    to { opacity: 1; }
}
input:-webkit-autofill {
    animation-name: autofill-detect;
    animation-duration: 0.01s;
}
`

function loginFields(): FieldGroup<any> {
    return [
        {
            name: 'usernameOrEmail',
            label: 'Email Address',
            required: true,
            asterisks: false,
            placeholder: 'example@gmail.com',
            validate: isEmail,
            component: textInputFieldComponentAdvanced({
                autoComplete: 'email',
                type: 'email',
            })
        },
        {
            name: 'password',
            label: 'Password',
            required: true,
            asterisks:false,
            placeholder: 'Enter your password',
            validate: emptyValidator,
            component: protectedInputFieldComponent({
                autoComplete: 'current-password'
            }, 'text', true)
        },
    ]
}

export const LoginForm = (props: Props) => {
    const { values = {}, invalid, form } = props

    const syncAutofilledValues = React.useCallback(() => {
        if (!form?.change) return
        const usernameInput = document.querySelector<HTMLInputElement>('input[name="usernameOrEmail"]')
        const passwordInput = document.querySelector<HTMLInputElement>('input[name="password"]')
        if (usernameInput?.value && usernameInput.value !== values.usernameOrEmail) {
            form.change('usernameOrEmail', usernameInput.value)
        }
        if (passwordInput?.value && passwordInput.value !== values.password) {
            form.change('password', passwordInput.value)
        }
    }, [form, values.password, values.usernameOrEmail])

    // Detect browser autofill so React state and LOG IN button stay in sync. Browsers don't fire
    // onChange for autofill; we use (1) animationstart (:-webkit-autofill) and (2) polling timeouts.
    React.useEffect(() => {
        const delays = [0, 100, 300, 600, 1000]
        const timeouts = delays.map(ms => window.setTimeout(syncAutofilledValues, ms))
        const onAnimationStart = (e: AnimationEvent) => {
            if (e.animationName === 'autofill-detect') syncAutofilledValues()
        }
        document.addEventListener('animationstart', onAnimationStart, true)
        return () => {
            timeouts.forEach(t => window.clearTimeout(t))
            document.removeEventListener('animationstart', onAnimationStart, true)
        }
    }, [syncAutofilledValues])

    const handleAnimationStart = React.useCallback((event: React.AnimationEvent<HTMLElement>) => {
        if (event.animationName === 'autofill-detect') {
            syncAutofilledValues()
        }
    }, [syncAutofilledValues])

    const isSubmitDisabled = !values.usernameOrEmail || !values.password || invalid
    return (
        <LoginFormWrapper onAnimationStart={handleAnimationStart}>
            <LogoHeading/>
            <h1 id='login-heading' className='LoginText'>Log In</h1>
            <GoogleAuthButton buttonText="Sign in with Google" />
            {renderFieldsSimple(loginFields(), true)}
            <FlexCol alignItems={'flex-start'}>
                <Link to={Paths.userForgot} aria-label='Forgot your password? Reset it here'>Forgot your password?</Link>
                <LoginSignButton
                    type='submit'
                    disabled={isSubmitDisabled}
                    aria-describedby={isSubmitDisabled ? 'login-submit-hint' : undefined}
                >
                    LOG IN
                </LoginSignButton>
                <span
                    id='login-submit-hint'
                    style={{ position: 'absolute', left: -9999, top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
                >
                    Enter your email and password to enable the LOG IN button.
                </span>
            </FlexCol>
        </LoginFormWrapper>
    )
}
